"use client";
// "use client" hoja interactiva: visibilidad por scroll (estado + listener window) — necesita cliente.

import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { localePath } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { cn } from "@/lib/utils";

/**
 * Mobile-only sticky conversion bar.
 * Appears after the hero scrolls out so the primary CTA stays one thumb away
 * (mobile-first conversion pattern, 2026 SaaS).
 * Glass chrome with refined micro-interactions.
 * 2026-09-23 TAREA F responsive-parity (excepción documentada, sin cambiar PC):
 * el desktop no tiene esta barra global — su conversión persistente es el
 * sticky contextual de Pricing (Pricing.tsx) + FinalCta; en 360px el CTA debe
 * seguir a un pulgar, así que el trigger se adapta (barra fija tras ~medio
 * viewport) pero el flujo es el mismo de PC (mismo destino /chat, mismo CTA
 * primario, sin pasos extra). La dedup con `body[data-pricing-sticky]`
 * garantiza una sola barra visible a la vez.
 */
/**
 * RUN12 F4 [sticky-threshold-finite-fallback] (2026-10-03,
 * SEED=2026-10-02-3df413b0; research 2026 react.dev 2026 "effects: guard
 * viewport reads — innerHeight 0 in SSR/embedded/zoom" — nombres solo
 * reporte, NUNCA copy): `Math.min(420, innerHeight*0.55)` con innerHeight
 * 0/NaN/Infinity (iframe encogido, zoom extremo, SSR) daba umbral 0/NaN y la
 * barra aparecía con cualquier scroll (ruido de conversión + CLS). Solo
 * number finito >0 entra al cálculo; resto → 420 (mismo default desktop).
 * Pura, sin copy. Fingerprint `sticky-threshold-finite-fallback`.
 */
export function resolveStickyThreshold(innerHeight: unknown): number {
  if (
    typeof innerHeight !== "number" ||
    !Number.isFinite(innerHeight) ||
    innerHeight <= 0
  )
    return 420;
  return Math.min(420, innerHeight * 0.55);
}

export function MobileStickyCta() {
  const t = useT();
  const locale = useLocale();
  const lp = (path: string) => localePath(locale, path);
  const [visible, setVisible] = useState(false);

  // MINIMALISTA PRO doble-sticky fix: si la barra sticky de Pricing está activa
  // (body[data-pricing-sticky="1]"), esta barra global se oculta — una sola barra.
  useEffect(() => {
    const compute = () => {
      let pricingSticky = false;
      try {
        pricingSticky = document.body?.dataset?.pricingSticky === "1";
      } catch {}
      setVisible(
        !pricingSticky &&
          window.scrollY > resolveStickyThreshold(window.innerHeight),
      );
    };
    const onScroll = () => {
      // ~ half a mobile viewport past the fold
      // 2026-09-27 FIX CPU budget (pestaña oculta: sin setState por scroll —
      // el tick de visibilidad ya re-evalúa al volver).
      if (document.hidden) return;
      compute();
    };
    const onPricingSticky = () => compute();
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pricing-sticky-change", onPricingSticky);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pricing-sticky-change", onPricingSticky);
    };
  }, []);

  if (typeof document !== "undefined") {
    try {
      if (visible && document.body?.dataset?.pricingSticky === "1") return null;
    } catch {}
  }

  return (
    <div
      className={cn("mobile-sticky-cta md:hidden", visible && "is-visible")}
      role="region"
      aria-label={t("heroCtaPrimary")}
      aria-hidden={!visible}
      // 2026-09-07 WCAG 2.2 2.4.3: barra oculta fuera del orden de Tab (opacity-0
      // seguía focuseable). inert la saca del árbol a11y hasta ser visible.
      inert={!visible}
      data-testid="mobile-sticky-cta"
    >
      <div className="flex items-center gap-3">
        {/* 2026-09-21 FASE 3 FIXER contraste (research 2026 WCAG 2.2 1.4.3 AA:
            blanco sobre el gradiente hero medía ~2.1:1 en la zona magenta —
            se cambia a accent sólido como CheckoutContent:673-676 (~14:1 en
            ambos temas), sin text-shadow ni brightness que laven el texto). */}
        <Link
          href={lp("/chat")}
          className="group flex min-h-12 flex-1 items-center justify-center gap-2.5 rounded-[var(--radius-button)] bg-[var(--accent)] px-5 text-[15px] font-semibold text-[var(--accent-foreground)] shadow-[0_10px_28px_-8px_var(--hero-focus,rgba(168,85,247,0.45))] transition-[transform,filter] duration-200 hover:-translate-y-px hover:brightness-110 active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hero-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none motion-reduce:transform-none"
        >
          <span>{t("chatTitle")}</span>
          {/* 2026-09-28 RUN5 FASE3 (SEED=2026-09-28-bae4824d, research 2026
              WCAG 2.2 reduced-motion: el guard del Link no casca al icono —
              su transition-transform propia seguía deslizando con
              reduced-motion. Guard propio, idéntico sin la preferencia). */}
          <ArrowRight
            className="size-4 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:transform-none"
            aria-hidden="true"
          />
        </Link>
      </div>
    </div>
  );
}
